import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { flyToCompleted } from '../../lib/mark-done';
import { cn } from '../../lib/utils';
import { useOverlays } from './overlay-context';

/**
 * A circle that marks the task done. It asks first, then fills with a tick.
 * Pass `done` and `onReopen` where finished tasks stay on screen, so the filled circle can reopen them.
 */
export default function CompleteButton({
  title,
  disabled = false,
  done = false,
  onComplete,
  onReopen,
}: {
  title: string;
  disabled?: boolean;
  done?: boolean;
  onComplete: () => Promise<void>;
  onReopen?: () => Promise<void>;
}) {
  const { confirm } = useOverlays();
  const ref = useRef<HTMLButtonElement>(null);
  const [marked, setMarked] = useState(false);
  const [asking, setAsking] = useState(false);
  const checked = done || marked;

  useEffect(() => {
    if (done) setMarked(false);
  }, [done]);
  const canReopen = done && Boolean(onReopen);

  function onClick() {
    setAsking(true);
    void confirm(
      canReopen
        ? { title: 'Reopen this task?', description: title, confirmLabel: 'Reopen' }
        : { title: 'Mark as done?', description: title, confirmLabel: 'Mark as done' },
    ).then((ok) => {
      setAsking(false);
      if (!ok) return;
      if (canReopen) {
        void onReopen?.();
        return;
      }
      setMarked(true);
      void flyToCompleted(ref.current);
      onComplete().catch(() => setMarked(false));
    });
  }

  return (
    <button
      ref={ref}
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={canReopen ? `Reopen: ${title}` : `Mark as done: ${title}`}
      title={disabled ? undefined : canReopen ? 'Reopen' : checked ? 'Done' : 'Mark as done'}
      disabled={disabled || asking || (checked && !canReopen)}
      onClick={onClick}
      className={cn(
        'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-[1.5px] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-default',
        checked
          ? 'border-emerald-600 bg-emerald-600 text-white'
          : 'border-muted-foreground/50 text-transparent hover:border-emerald-600 hover:bg-emerald-50 hover:text-emerald-600 dark:hover:bg-emerald-950 dark:hover:text-emerald-400',
        canReopen && 'hover:border-emerald-700 hover:bg-emerald-700',
      )}
    >
      <Check className="h-3 w-3" strokeWidth={3} aria-hidden />
    </button>
  );
}
